import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui'
import {
  DocumentTextIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  TrashIcon,
  PlusIcon,
  ShieldCheckIcon,
  DocumentCheckIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import { formatDate } from '../../utils/formatters'
import toast from 'react-hot-toast'
import { api } from '../../services/api'
import { ServiceProviderDocument, DocumentStatus, ServiceType, DocumentType } from '../../types'

// Same requirements configuration as registration
const serviceDocumentRequirements = {
  // Service types (from registration)
  AMBULANCE_TRANSPORT: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT', 'IDENTIFICATION'],
    optional: ['OPERATING_PERMIT', 'VEHICLE_REGISTRATION'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Medical/Emergency Services Certification',
      INSURANCE_DOCUMENT: 'Vehicle and Liability Insurance',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      OPERATING_PERMIT: 'Transport Operating Permit',
      VEHICLE_REGISTRATION: 'Ambulance Vehicle Registration Documents'
    }
  },
  GHUSL_SERVICE: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION'],
    optional: ['ISLAMIC_QUALIFICATION', 'CHARACTER_REFERENCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Ghusl Services Certification',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport',
      ISLAMIC_QUALIFICATION: 'Islamic Qualification Certificates',
      CHARACTER_REFERENCE: 'Character Reference Letters',
      OPERATING_PERMIT: 'Religious Services Operating Permit'
    }
  },
  KAFAN_SERVICE: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION'],
    optional: ['ISLAMIC_QUALIFICATION', 'CHARACTER_REFERENCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Kafan Services Certification',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport',
      ISLAMIC_QUALIFICATION: 'Islamic Qualification Certificates',
      CHARACTER_REFERENCE: 'Character Reference Letters'
    }
  },
  JANAZAH_LEAD: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION'],
    optional: ['ISLAMIC_QUALIFICATION', 'CHARACTER_REFERENCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Janazah Leadership Certification',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport',
      ISLAMIC_QUALIFICATION: 'Islamic Qualification Certificates',
      CHARACTER_REFERENCE: 'Character Reference Letters'
    }
  },
  BURIAL_PLOT: {
    required: ['BUSINESS_LICENSE', 'OPERATING_PERMIT', 'IDENTIFICATION'],
    optional: ['LAND_OWNERSHIP', 'ENVIRONMENTAL_CLEARANCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Burial Services Management Certification (Optional)',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      OPERATING_PERMIT: 'Burial Plot Operating Permit',
      LAND_OWNERSHIP: 'Land Ownership or Lease Documents',
      ENVIRONMENTAL_CLEARANCE: 'Environmental Clearance Certificate'
    }
  },
  CATERING: {
    required: ['BUSINESS_LICENSE', 'FOOD_HANDLER_CERT', 'IDENTIFICATION'],
    optional: ['HALAL_CERTIFICATION', 'KITCHEN_FACILITY_LICENSE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      FOOD_HANDLER_CERT: 'Food Handler Certificate',
      IDENTIFICATION: 'IC/Passport of Food Handlers',
      HALAL_CERTIFICATION: 'Halal Certification',
      KITCHEN_FACILITY_LICENSE: 'Kitchen Facility License',
      PROFESSIONAL_CERTIFICATE: 'Food Handler Certificate',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)',
      OPERATING_PERMIT: 'Food Business Operating Permit'
    }
  },
  FLOWERS: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['SUPPLIER_CONTRACTS', 'QUALITY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      SUPPLIER_CONTRACTS: 'Flower Supplier Contracts',
      QUALITY_CERTIFICATIONS: 'Quality Assurance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  },
  TENT_RENTAL: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['EQUIPMENT_REGISTRATION', 'SAFETY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      EQUIPMENT_REGISTRATION: 'Equipment Registration Documents',
      SAFETY_CERTIFICATIONS: 'Safety Compliance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  },
  PHOTOGRAPHY: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['PORTFOLIO', 'PROFESSIONAL_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      PORTFOLIO: 'Photography Portfolio',
      PROFESSIONAL_CERTIFICATIONS: 'Photography Professional Certifications',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)'
    }
  },
  YASIN_BOOKS: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['PUBLISHER_AGREEMENTS', 'QUALITY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      PUBLISHER_AGREEMENTS: 'Publisher Distribution Agreements',
      QUALITY_CERTIFICATIONS: 'Quality Assurance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  },
  ISLAMIC_SOUVENIRS: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['SUPPLIER_CONTRACTS', 'QUALITY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      SUPPLIER_CONTRACTS: 'Supplier Contracts',
      QUALITY_CERTIFICATIONS: 'Quality Assurance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  },
  // Provider types (legacy support)
  AMBULANCE_PROVIDER: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT', 'IDENTIFICATION'],
    optional: ['OPERATING_PERMIT', 'VEHICLE_REGISTRATION'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Medical/Emergency Services Certification',
      INSURANCE_DOCUMENT: 'Vehicle and Liability Insurance',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      OPERATING_PERMIT: 'Transport Operating Permit',
      VEHICLE_REGISTRATION: 'Ambulance Vehicle Registration Documents'
    }
  },
  USTADZ: {
    required: ['PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION'],
    optional: ['BUSINESS_LICENSE', 'SERVICE_CERTIFICATION'],
    descriptions: {
      PROFESSIONAL_CERTIFICATE: 'Islamic Studies Certification/Sijil',
      IDENTIFICATION: 'IC/Passport',
      BUSINESS_LICENSE: 'Business Registration (if applicable)',
      SERVICE_CERTIFICATION: 'Religious Service Authorization'
    }
  },
  CEMETERY_PROVIDER: {
    required: ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT'],
    optional: ['OPERATING_PERMIT', 'PROFESSIONAL_CERTIFICATE'],
    descriptions: {
      BUSINESS_LICENSE: 'Cemetery Operating License',
      INSURANCE_DOCUMENT: 'Public Liability Insurance',
      OPERATING_PERMIT: 'Land Use Permit',
      PROFESSIONAL_CERTIFICATE: 'Cemetery Management Certification'
    }
  },
  FUNERAL_HOME: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT'],
    optional: ['OPERATING_PERMIT', 'SERVICE_CERTIFICATION'],
    descriptions: {
      BUSINESS_LICENSE: 'Funeral Home Business License',
      PROFESSIONAL_CERTIFICATE: 'Funeral Director Certification',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance',
      OPERATING_PERMIT: 'Health Department Permit',
      SERVICE_CERTIFICATION: 'Mortuary Service License'
    }
  },
  VENDOR: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      PROFESSIONAL_CERTIFICATE: 'Professional Certification',
      INSURANCE_DOCUMENT: 'Public Liability Insurance'
    }
  },
  PROVIDER: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      PROFESSIONAL_CERTIFICATE: 'Professional Certification',
      INSURANCE_DOCUMENT: 'Public Liability Insurance'
    }
  },
  OPERATOR_TRAINING: {
    required: ['IC_PASSPORT', 'SSM_CERTIFICATE'],
    optional: [],
    descriptions: {
      IC_PASSPORT: 'IC/Passport',
      SSM_CERTIFICATE: 'SSM/Company Incorporation Certificate'
    }
  },
  OPERATOR_OPERATION: {
    required: ['UTILITY_BILL', 'SELFIE_PHOTO'],
    optional: [],
    descriptions: {
      UTILITY_BILL: 'Utility Bill (e.g., TNB, Air Selangor, Syabas)',
      SELFIE_PHOTO: 'Selfie Photo for ID Verification'
    }
  }
}

interface DocumentManagementTabProps {
  serviceType: ServiceType
}

export default function DocumentManagementTab({ serviceType }: DocumentManagementTabProps) {
  const [documents, setDocuments] = useState<ServiceProviderDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState<string | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedDocType, setSelectedDocType] = useState<DocumentType | ''>('')
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [selectedDocumentForPreview, setSelectedDocumentForPreview] = useState<ServiceProviderDocument | null>(null)

  useEffect(() => {
    fetchDocuments()
  }, [])

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/documents/provider/documents')
      setDocuments(response.data)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch documents')
    } finally {
      setLoading(false)
    }
  }

  const getDocumentRequirements = (serviceType: string) => {
    if (!serviceType || !serviceDocumentRequirements[serviceType as keyof typeof serviceDocumentRequirements]) {
      return { required: [], optional: [], descriptions: {} }
    }
    return serviceDocumentRequirements[serviceType as keyof typeof serviceDocumentRequirements]
  }

  const getDocumentDescription = (docType: string) => {
    const requirements = getDocumentRequirements(serviceType)
    return requirements.descriptions[docType] || docType.replace(/_/g, ' ')
  }

  const getDocumentRequirement = (docType: string) => {
    const requirements = getDocumentRequirements(serviceType)
    if (requirements?.required.includes(docType)) {
      return { type: 'required', color: 'text-red-600', bg: 'bg-red-50', label: 'Required' }
    } else if (requirements?.optional.includes(docType)) {
      return { type: 'optional', color: 'text-blue-600', bg: 'bg-blue-50', label: 'Optional' }
    }
    return { type: 'unknown', color: 'text-gray-600', bg: 'bg-gray-50', label: 'Additional' }
  }

  const getStatusColor = (status: DocumentStatus): string => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'UPLOADED':
        return 'bg-blue-100 text-blue-800'
      case 'VERIFIED':
        return 'bg-green-100 text-green-800'
      case 'REJECTED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'PENDING':
        return <ClockIcon className="h-4 w-4" />
      case 'UPLOADED':
        return <DocumentTextIcon className="h-4 w-4" />
      case 'VERIFIED':
        return <CheckCircleIcon className="h-4 w-4" />
      case 'REJECTED':
        return <XCircleIcon className="h-4 w-4" />
      default:
        return <DocumentTextIcon className="h-4 w-4" />
    }
  }

  const handleFileUpload = async (docType: DocumentType, file: File) => {
    if (!file) return

    setUploading(docType)
    const formData = new FormData()
    formData.append('document', file)
    formData.append('documentType', docType)

    try {
      await api.post('/documents/provider/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      toast.success('Document uploaded successfully')
      await fetchDocuments()
      setShowUploadModal(false)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to upload document')
    } finally {
      setUploading(null)
    }
  }

  const handleDownloadDocument = async (document: ServiceProviderDocument) => {
    try {
      const response = await api.get(`/documents/provider/${document.id}/download`, {
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = document.originalName || `Document_${document.id}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      toast.error('Failed to download document')
    }
  }

  const handleDeleteDocument = async (document: ServiceProviderDocument) => {
    if (!confirm('Are you sure you want to delete this document?')) return

    try {
      await api.delete(`/documents/provider/documents/${document.id}`)
      toast.success('Document deleted successfully')
      await fetchDocuments()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete document')
    }
  }

  const handlePreviewDocument = (document: ServiceProviderDocument) => {
    setSelectedDocumentForPreview(document)
  }

  const requirements = getDocumentRequirements(serviceType)
  const allRequiredDocs = [...requirements.required, ...requirements.optional]
  const uploadedDocTypes = documents.map(doc => doc.type)

  const getDocumentStatus = (docType: string) => {
    const doc = documents.find(d => d.type === docType)
    return doc?.status || 'NOT_UPLOADED'
  }

  const getStats = () => {
    const total = allRequiredDocs.length
    const verified = documents.filter(d => d.status === 'VERIFIED').length
    const pending = documents.filter(d => d.status === 'PENDING' || d.status === 'UPLOADED').length
    const rejected = documents.filter(d => d.status === 'REJECTED').length

    // Debug logging
    console.log('Documents:', documents)
    console.log('Document statuses:', documents.map(d => ({ id: d.id, type: d.type, status: d.status })))
    console.log('Stats calculation:', { total, verified, pending, rejected })

    return { total, verified, pending, rejected }
  }

  const stats = getStats()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Document Management</h2>
          <p className="text-gray-600 mt-1">Manage your verification documents</p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          <CloudArrowUpIcon className="h-4 w-4" />
          <span>Upload Document</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-gray-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-gray-600">Total Required</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-green-600">{stats.verified}</p>
              <p className="text-gray-600">Verified</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-gray-600">Pending Review</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              <p className="text-gray-600">Rejected</p>
            </div>
          </div>
        </div>
      </div>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle>Required Documentation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {allRequiredDocs.map((docType) => {
              const status = getDocumentStatus(docType)
              const requirement = getDocumentRequirement(docType)
              const document = documents.find(d => d.type === docType)
              const isUploaded = uploadedDocTypes.includes(docType)

              return (
                <div key={docType} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-medium text-gray-900">
                          {getDocumentDescription(docType)}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${requirement.bg} ${requirement.color}`}>
                          {requirement.type === 'required' ? (
                            <><ShieldCheckIcon className="h-3 w-3 mr-1" /> {requirement.label}</>
                          ) : (
                            <><DocumentCheckIcon className="h-3 w-3 mr-1" /> {requirement.label}</>
                          )}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status as DocumentStatus)}`}>
                          {getStatusIcon(status as DocumentStatus)}
                          <span className="ml-1">
                            {status === 'NOT_UPLOADED' ? 'Not Uploaded' : status.replace(/_/g, ' ')}
                          </span>
                        </span>
                      </div>

                      {document?.verificationNotes && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                          <div className="flex items-start space-x-2">
                            <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-amber-900">Review Notes:</p>
                              <p className="text-sm text-amber-700">{document.verificationNotes}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {isUploaded && document && (
                        <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                          <span>Uploaded: {formatDate(document.uploadedAt)}</span>
                          <span>Size: {(document.fileSize / 1024).toFixed(1)} KB</span>
                          {document.verifiedAt && (
                            <span>Verified: {formatDate(document.verifiedAt)}</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      {isUploaded && document ? (
                        <>
                          <button
                            onClick={() => handleDownloadDocument(document)}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Download document"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </button>
                          {status === 'REJECTED' && (
                            <button
                              onClick={() => {
                                setSelectedDocType(docType)
                                setShowUploadModal(true)
                              }}
                              className="p-2 text-red-400 hover:text-red-600 transition-colors"
                              title="Re-upload document"
                            >
                              <CloudArrowUpIcon className="h-4 w-4" />
                            </button>
                          )}
                          {status !== 'VERIFIED' && (
                            <button
                              onClick={() => handleDeleteDocument(document)}
                              className="p-2 text-red-400 hover:text-red-600 transition-colors"
                              title="Delete document"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedDocType(docType)
                            setShowUploadModal(true)
                          }}
                          className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors flex items-center space-x-1"
                        >
                          <PlusIcon className="h-3 w-3" />
                          <span>Upload</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}

            {allRequiredDocs.length === 0 && (
              <div className="text-center py-8">
                <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No documents required</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No specific document requirements for your service type.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Documents Section - Show Uploaded Documents */}
      {documents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {documents.map((document) => (
                <div key={document.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(document.status)}`}>
                          {getStatusIcon(document.status)}
                          <span className="ml-1">{document.status.replace(/_/g, ' ')}</span>
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800`}>
                          {document.type.replace(/_/g, ' ')}
                        </span>
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {document.originalName || `Document ${document.id}`}
                        </h3>
                      </div>

                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        <span>Uploaded {new Date(document.uploadedAt || document.createdAt).toLocaleDateString()}</span>
                        {document.fileSize && <span>File size: {(document.fileSize / 1024).toFixed(1)} KB</span>}
                        {document.verificationNotes && (
                          <span className="text-amber-600">Has review notes</span>
                        )}
                      </div>

                      {document.verificationNotes && (
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                          <div className="flex items-start space-x-2">
                            <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-amber-900">Review Notes:</p>
                              <p className="text-sm text-amber-700">{document.verificationNotes}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePreviewDocument(document)}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <EyeIcon className="h-3 w-3 mr-1" />
                        View
                      </button>
                      <button
                        onClick={() => handleDownloadDocument(document)}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
                        Download
                      </button>
                      {document.status !== 'VERIFIED' && (
                        <button
                          onClick={() => handleDeleteDocument(document)}
                          disabled={uploading === document.id}
                          className="inline-flex items-center px-3 py-1 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                        >
                          <TrashIcon className="h-3 w-3 mr-1" />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {selectedDocType ? `Replace ${getDocumentDescription(selectedDocType)}` : 'Upload Document'}
            </h3>

            {!selectedDocType && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Document Type
                </label>
                <select
                  value={selectedDocType}
                  onChange={(e) => setSelectedDocType(e.target.value as DocumentType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Choose document type...</option>
                  {allRequiredDocs.map((docType) => {
                    const isUploaded = uploadedDocTypes.includes(docType)
                    return (
                      <option
                        key={docType}
                        value={docType}
                        disabled={isUploaded && getDocumentStatus(docType) !== 'REJECTED'}
                      >
                        {getDocumentDescription(docType)} {isUploaded ? '(Uploaded)' : ''}
                      </option>
                    )
                  })}
                </select>
              </div>
            )}

            {selectedDocType && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choose File
                </label>
                <input
                  type="file"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleFileUpload(selectedDocType, e.target.files[0])
                    }
                  }}
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  disabled={uploading === selectedDocType}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Accepted formats: PDF, JPG, JPEG, PNG (Max 10MB)
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUploadModal(false)
                  setSelectedDocType('')
                }}
                disabled={uploading !== null}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>

            {uploading && (
              <div className="mt-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-sm text-gray-600 mt-2">Uploading document...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {selectedDocumentForPreview && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setSelectedDocumentForPreview(null)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <div className="px-6 py-4 bg-white border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Document Preview
                    </h3>
                    <p className="text-sm text-gray-500">
                      {selectedDocumentForPreview.originalName}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedDocumentForPreview(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 bg-white">
                <div className="mb-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {selectedDocumentForPreview.type?.replace('_', ' ')}
                  </span>
                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedDocumentForPreview.status)}`}>
                    {selectedDocumentForPreview.status}
                  </span>
                </div>

                {selectedDocumentForPreview.filePath && (
                  <div className="border rounded-lg overflow-hidden" style={{ height: '500px' }}>
                    {selectedDocumentForPreview.mimeType?.includes('image') ? (
                      <img
                        src={selectedDocumentForPreview.filePath}
                        alt="Document preview"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <iframe
                        src={selectedDocumentForPreview.filePath}
                        className="w-full h-full"
                        title="Document preview"
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t flex justify-end space-x-3">
                <button
                  onClick={() => setSelectedDocumentForPreview(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => handleDownloadDocument(selectedDocumentForPreview)}
                  className="px-4 py-2 text-white bg-primary-600 hover:bg-primary-700 rounded-md font-medium transition-colors"
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}